import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Plus } from "lucide-react";
import { api, getToken } from "@/lib/api";
import { fmtDate, inputCls, leadsChanged, StatusPill } from "@/lib/billing";
import { Btn, Spinner } from "@/components/PortalPage";
import { CustomerPicker } from "@/pages/billing/DocEditor";
import { IntakeLinks } from "@/pages/billing/Customers";

const NewLead = ({ onSaved, onCancel }) => {
  const [f, setF] = useState({ customer_id: "", title: "", details: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api("/billing/leads", { method: "POST", body: f }); toast.success("Lead added"); leadsChanged(); onSaved(); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3 space-y-2 rounded-lg border border-[#333] p-3" data-testid="lead-form">
      <CustomerPicker value={f.customer_id} onChange={(v) => setF({ ...f, customer_id: v })} />
      <input className={inputCls} placeholder="What do they need? (e.g. Graffiti on brick wall)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="lead-title-input" />
      <textarea className={inputCls} rows={2} placeholder="Details, address, best time to call…" value={f.details} onChange={(e) => setF({ ...f, details: e.target.value })} data-testid="lead-details-input" />
      <div className="flex gap-2">
        <Btn onClick={save} busy={busy} disabled={!f.customer_id || !f.title.trim()} data-testid="lead-save-btn">SAVE LEAD</Btn>
        <Btn variant="outline" onClick={onCancel} data-testid="lead-cancel-btn">CANCEL</Btn>
      </div>
    </div>
  );
};

const LeadPhotos = ({ l }) => {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    Promise.all((l.photos || []).map((p) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/billing/leads/${l.id}/photos/${p.id}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.blob() : null)).then((b) => b && URL.createObjectURL(b)).catch(() => null))).then((u) => setUrls(u.filter(Boolean)));
  }, [l]);
  if (!urls.length) return null;
  return <div className="mt-2 flex flex-wrap gap-2" data-testid={`lead-photos-${l.id}`}>{urls.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="Job" className="h-14 w-14 rounded-md border border-[#333] object-cover" /></a>)}</div>;
};

const LeadRow = ({ l, onChanged }) => {
  const nav = useNavigate();
  const toQuote = async () => { try { const q = await api(`/billing/leads/${l.id}/quote`, { method: "POST" }); leadsChanged(); nav(`/billing/doc/${q.id}`); } catch (e) { toast.error(e.message); } };
  const archive = async () => { try { await api(`/billing/leads/${l.id}/status?status=${l.status === "archived" ? "new" : "archived"}`, { method: "POST" }); leadsChanged(); onChanged(); } catch (e) { toast.error(e.message); } };
  return (
    <div className="flex flex-wrap items-center gap-3 py-3" data-testid={`lead-row-${l.id}`}>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold">{l.customer_name} · {l.title || "Untitled request"}</p>
        <p className="truncate text-xs text-[#888]">{l.requested_at ? fmtDate(l.requested_at) : fmtDate(l.created_at)}{l.source === "jobber" ? " · from Jobber" : ""}{l.source === "website" ? " · from your online form" : ""}{l.details ? ` · ${l.details}` : ""}</p>
        <LeadPhotos l={l} />
      </div>
      <StatusPill status={l.status} />
      {l.quote_id
        ? <button onClick={() => nav(`/billing/doc/${l.quote_id}`)} className="text-xs font-bold text-[var(--accent)]" data-testid={`lead-open-quote-${l.id}`}>Open quote</button>
        : l.status !== "archived" && <button onClick={toQuote} className="flex items-center gap-1 text-xs font-bold text-[var(--accent)]" data-testid={`lead-quote-${l.id}`}><FileText size={13} />Create quote</button>}
      <button onClick={archive} className="text-xs font-bold text-[#888]" data-testid={`lead-archive-${l.id}`}>{l.status === "archived" ? "Restore" : "Archive"}</button>
    </div>
  );
};

export const LeadsTab = () => {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api("/billing/leads").then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Spinner />;
  return (
    <div data-testid="leads-tab">
      <div className="mb-3"><IntakeLinks /></div>
      {adding ? <NewLead onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
        : <button onClick={() => setAdding(true)} data-testid="lead-new-btn" className="mb-2 flex items-center gap-1.5 rounded-lg border border-[var(--accent)] px-3.5 py-2 text-xs font-extrabold text-[var(--accent)]"><Plus size={14} />NEW LEAD</button>}
      {!rows.length ? <p className="py-8 text-center text-sm text-[#888]" data-testid="leads-empty">No leads yet. Add one, or import your Jobber requests in Setup.</p>
        : <div className="divide-y divide-[#262626]" data-testid="leads-list">{rows.map((l) => <LeadRow key={l.id} l={l} onChanged={load} />)}</div>}
    </div>
  );
};
